import { useMemo, useState } from 'react';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import { format, isValid, parseISO } from 'date-fns';
import { capitalize, isEmpty, sortBy } from 'lodash';
import PropTypes from 'prop-types';

import NothingFound from 'common/components/NothingFound';
import RichTextViewer from 'common/components/RichTextViewer';

const safeDate = (value) => {
  if (!value) return null;
  const d = parseISO(value);
  return isValid(d) ? d : null;
};

const splitAuthorName = (fullName) => {
  if (!fullName) return { firstName: '', lastName: '' };
  const trimmed = String(fullName).trim();
  if (!trimmed) return { firstName: '', lastName: '' };
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
};

const normalizeNote = (note) => {
  if (!note) return null;
  if (note.noteId || note.noteContent || note.noteAuthor) {
    return { ...note, parsedDate: safeDate(note.noteCreatedAt) };
  }
  const { firstName, lastName } = splitAuthorName(note.authorName);
  return {
    noteId: note.id,
    noteContent: note.content,
    noteState: note.state,
    noteCreatedAt: note.createdAt,
    noteAuthor: {
      firstName,
      lastName,
      pomUserId: note.authorId || '',
    },
    noteContextType: note.contextType,
    noteContextId: note.contextId,
    parsedDate: safeDate(note.createdAt),
  };
};

const ShipmentNotesTimelineDialog = ({ open, onClose, notes, referenceId }) => {
  const [displayEmpty, setDisplayEmpty] = useState(false);

  const sortedNotes = useMemo(() => {
    const enriched = notes.map(normalizeNote).filter((n) => n && n.parsedDate);
    return sortBy(enriched, (n) => n.parsedDate.getTime());
  }, [notes]);

  const headerTitle = referenceId ? `Shipping Notes — ${referenceId}` : 'Shipping Notes';

  const handleClose = () => {
    setDisplayEmpty(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      onTransitionEnter={() => setDisplayEmpty(true)}
      maxWidth='md'
      fullWidth
      PaperProps={{ sx: { minHeight: 360 } }}
    >
      <DialogTitle
        id='ShipmentNotesTimeline-Dialog-header'
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <StickyNote2Icon size={24} />
        <Box sx={{ flex: 1 }}>{headerTitle}</Box>
        <IconButton
          onClick={handleClose}
          aria-label='Close Dialog'
          size='small'
          sx={{ color: 'white' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {isEmpty(sortedNotes) && displayEmpty && <NothingFound height='20vh' />}

        {sortedNotes.map((note, idx) => {
          const author = note.noteAuthor || {};
          const fullName = `${author.firstName ?? ''} ${author.lastName ?? ''}`.trim();
          const isLast = idx === sortedNotes.length - 1;
          const orderRefId = note.noteContextId || null;
          return (
            <Box
              key={note.noteId}
              sx={{
                display: 'flex',
                alignItems: 'stretch',
                px: 2,
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box
                sx={{
                  width: 110,
                  flexShrink: 0,
                  textAlign: 'right',
                  pr: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  pt: 0.25,
                }}
              >
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ display: 'block', lineHeight: 1.5, fontSize: '0.7rem' }}
                >
                  {format(note.parsedDate, 'MM/dd/yyyy')}
                </Typography>
                <Typography
                  variant='body2'
                  color='primary.main'
                  fontWeight={700}
                  sx={{ display: 'block', lineHeight: 1.5, fontSize: '0.7rem' }}
                >
                  {format(note.parsedDate, 'hh:mm:ss a')}
                </Typography>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{
                    display: 'block',
                    lineHeight: 1.4,
                    fontStyle: 'italic',
                    fontSize: '0.65rem',
                  }}
                >
                  {capitalize(fullName) || 'Unknown'}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  flexShrink: 0,
                  width: 20,
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    flexShrink: 0,
                    mt: 0.4,
                  }}
                />
                {!isLast && (
                  <Box
                    sx={{
                      width: 2,
                      flex: 1,
                      bgcolor: 'grey.300',
                      mt: 0.5,
                      minHeight: 12,
                    }}
                  />
                )}
              </Box>

              <Box sx={{ flex: 1, minWidth: 0, pl: 1.5 }}>
                {orderRefId && (
                  <Typography
                    variant='subtitle2'
                    color='primary.main'
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      lineHeight: 1.2,
                      mb: 0.75,
                    }}
                  >
                    Order #{orderRefId}
                  </Typography>
                )}

                <Box sx={{ wordBreak: 'break-word' }}>
                  <RichTextViewer
                    value={note.noteContent}
                    id={`ShipmentNoteTimeline-${note.noteId}`}
                  />
                </Box>
              </Box>
            </Box>
          );
        })}
      </DialogContent>
    </Dialog>
  );
};

ShipmentNotesTimelineDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  notes: PropTypes.arrayOf(
    PropTypes.shape({
      noteId: PropTypes.string,
      noteContent: PropTypes.string,
      noteCreatedAt: PropTypes.string,
      noteContextId: PropTypes.string,
      noteAuthor: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        pomUserId: PropTypes.string,
      }),
      id: PropTypes.string,
      content: PropTypes.string,
      createdAt: PropTypes.string,
      authorName: PropTypes.string,
      authorId: PropTypes.string,
      state: PropTypes.string,
      contextType: PropTypes.string,
      contextId: PropTypes.string,
    })
  ),
  referenceId: PropTypes.string,
};

ShipmentNotesTimelineDialog.defaultProps = {
  notes: [],
  referenceId: undefined,
};

export default ShipmentNotesTimelineDialog;
